import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const StringingPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', troubleshooting: 'トラブル対策', title: '糸引き（ストリンギング）' },
    badge: '中程度',
    title: '糸引き（ストリンギング）',
    description: 'ノズル移動時に細い糸状の樹脂が残る現象。見た目を損ねるが、後処理で除去可能。',
    symptoms: {
      title: '症状の例',
      items: [
        '造形物の間に細い糸状の樹脂が張る',
        '移動経路に沿って樹脂の筋が残る',
        '表面に細かい毛羽立ちが発生する',
        '複数パーツの同時造形で特に顕著'
      ]
    },
    causes: {
      title: '主な原因',
      items: [
        { title: '1. リトラクション設定不足', desc: 'リトラクション（引き戻し）の距離や速度が不適切だと、ノズル移動時に樹脂が垂れて糸引きが発生します。' },
        { title: '2. ノズル温度過高', desc: '温度が高すぎると樹脂の粘度が下がり、ノズルから垂れやすくなります。' },
        { title: '3. 移動速度が遅い', desc: 'ノズルの移動速度が遅いと、移動中に樹脂が垂れる時間が長くなり、糸引きが発生しやすくなります。' },
        { title: '4. フィラメントの吸湿', desc: 'フィラメントが湿気を吸うと、加熱時に水蒸気が発生し、糸引きの原因となります。' }
      ]
    },
    solutions: {
      title: '対策・チェックポイント',
      items: [
        {
          title: 'リトラクション設定の最適化',
          desc: 'フィラメントとプリンターに応じて調整します。',
          points: ['ダイレクトドライブ: 0.5-2mm', 'ボーデンチューブ: 4-8mm', 'リトラクション速度: 25-45mm/s', '少しずつ調整してテストする']
        },
        {
          title: 'ノズル温度の下降',
          desc: '推奨温度範囲の下限から試します。',
          points: ['5°C刻みで下げてテスト', '層間接着が弱くならない範囲で調整', '温度タワーテストが有効']
        },
        {
          title: '移動速度の向上',
          desc: 'ノズルの移動速度を上げることで糸引きを軽減します。',
          points: ['トラベル速度: 150-200mm/s', 'Z軸ホップを有効化（必要に応じて）', 'コーミングモードの活用']
        },
        {
          title: 'フィラメントの乾燥',
          desc: '特にPETG、ナイロンなど吸湿しやすい材料で重要です。',
          points: ['フィラメントドライヤーで乾燥（50-60°C、4-6時間）', '密閉容器に乾燥剤と一緒に保管', '開封後は早めに使い切る']
        },
        {
          title: 'スライサー設定の調整',
          desc: 'スライサーの詳細設定で糸引きを抑制します。',
          points: ['最小移動距離の設定', 'ワイプ機能の有効化', 'コースト設定の調整']
        }
      ]
    },
    postProcess: {
      title: '後処理方法',
      desc: '糸引きが発生してしまった場合の除去方法：',
      items: [
        'ヒートガンで軽く炙る（PLAなど低温材料）',
        'カッターやニッパーで切り取る',
        'サンドペーパーで研磨',
        '手で引きちぎる（細かい糸の場合）'
      ]
    },
    related: {
      title: '関連情報',
      items: [
        { title: 'PETG フィラメント', desc: '糸引きが発生しやすい材料', link: '/filament/petg' },
        { title: 'TPU フィラメント', desc: '柔軟材料での糸引き対策', link: '/filament/tpu' }
      ]
    },
    backLink: 'トラブル対策一覧に戻る'
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', troubleshooting: 'Troubleshooting', title: 'Stringing' },
    badge: 'Moderate',
    title: 'Stringing',
    description: 'Thin threads of filament left behind during nozzle travel moves. Affects appearance but can be removed in post-processing.',
    symptoms: {
      title: 'Symptoms',
      items: [
        'Thin filament threads between printed parts',
        'Filament strands along travel paths',
        'Fine hair-like strands on surface',
        'Especially noticeable when printing multiple parts'
      ]
    },
    causes: {
      title: 'Main Causes',
      items: [
        { title: '1. Insufficient Retraction Settings', desc: 'Improper retraction distance or speed causes filament to ooze during travel moves, resulting in stringing.' },
        { title: '2. Nozzle Temperature Too High', desc: 'Excessive temperature reduces filament viscosity, making it more prone to dripping from the nozzle.' },
        { title: '3. Slow Travel Speed', desc: 'Slow nozzle movement gives more time for filament to ooze during travel, increasing stringing.' },
        { title: '4. Filament Moisture Absorption', desc: 'Moisture in filament creates steam when heated, contributing to stringing issues.' }
      ]
    },
    solutions: {
      title: 'Solutions & Checkpoints',
      items: [
        {
          title: 'Optimize Retraction Settings',
          desc: 'Adjust according to filament and printer type.',
          points: ['Direct drive: 0.5-2mm', 'Bowden tube: 4-8mm', 'Retraction speed: 25-45mm/s', 'Adjust gradually and test']
        },
        {
          title: 'Lower Nozzle Temperature',
          desc: 'Start from the lower end of recommended temperature range.',
          points: ['Decrease in 5°C increments and test', 'Adjust within range that maintains layer adhesion', 'Temperature tower tests are effective']
        },
        {
          title: 'Increase Travel Speed',
          desc: 'Faster nozzle movement reduces stringing.',
          points: ['Travel speed: 150-200mm/s', 'Enable Z-hop if needed', 'Use combing mode']
        },
        {
          title: 'Dry Filament',
          desc: 'Especially important for hygroscopic materials like PETG and nylon.',
          points: ['Dry with filament dryer (50-60°C, 4-6 hours)', 'Store in sealed container with desiccant', 'Use soon after opening']
        },
        {
          title: 'Adjust Slicer Settings',
          desc: 'Use advanced slicer settings to reduce stringing.',
          points: ['Set minimum travel distance', 'Enable wipe feature', 'Adjust coast settings']
        }
      ]
    },
    postProcess: {
      title: 'Post-Processing Methods',
      desc: 'Methods to remove stringing after printing:',
      items: [
        'Lightly heat with heat gun (for low-temp materials like PLA)',
        'Cut off with knife or nippers',
        'Sand with sandpaper',
        'Pull off by hand (for fine threads)'
      ]
    },
    related: {
      title: 'Related Information',
      items: [
        { title: 'PETG Filament', desc: 'Material prone to stringing', link: '/filament/petg' },
        { title: 'TPU Filament', desc: 'Stringing solutions for flexible materials', link: '/filament/tpu' }
      ]
    },
    backLink: 'Back to Troubleshooting List'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.troubleshooting, path: `${prefix}/troubleshooting` },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* Page Header */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="tech-label bg-[#4c7ca6] text-white">{TEXT.badge}</span>
                <h1 className="text-4xl font-bold text-white font-['Exo']">
                  {TEXT.title}
                </h1>
              </div>
              <p className="text-metal-light text-lg">
                {TEXT.description}
              </p>
              <div className="section-divider"></div>
            </div>

            {/* 症状の例 */}
            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.symptoms.title}
              </h2>
              <ul className="space-y-3 text-metal-light">
                {TEXT.symptoms.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-arrow-right-s-line text-accent mt-1"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 主な原因 */}
            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.causes.title}
              </h2>
              <div className="space-y-4">
                {TEXT.causes.items.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-l-[#4c7ca6] pl-4">
                    <h3 className="text-lg font-semibold text-accent font-['Exo'] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 対策・チェックポイント */}
            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.solutions.title}
              </h2>
              <div className="space-y-4">
                {TEXT.solutions.items.map((item, idx) => (
                  <div key={idx} className="clickable-card p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] text-xl mt-1"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Exo'] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-metal-light text-sm mb-2">
                          {item.desc}
                        </p>
                        <ul className="text-metal-light text-sm space-y-1 ml-4">
                          {item.points.map((point, pidx) => (
                            <li key={pidx}>• {point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 後処理方法 */}
            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.postProcess.title}
              </h2>
              <div className="space-y-3 text-metal-light">
                <p>{TEXT.postProcess.desc}</p>
                <ul className="space-y-2 ml-4">
                  {TEXT.postProcess.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 関連情報 */}
            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.related.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEXT.related.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={`${prefix}${item.link}`}
                    className="clickable-card p-4 hover:border-l-4 hover:border-l-accent transition-all"
                  >
                    <h3 className="text-white font-['Exo'] font-semibold mb-1">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Back Link */}
            <div className="flex justify-center">
              <Link
                to={`${prefix}/troubleshooting`}
                className="flex items-center space-x-2 text-accent hover:text-white transition-colors font-['Roboto_Mono']"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{TEXT.backLink}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default StringingPage;
